import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { EcommerceNavigation, HeaderEcommerce } from '@payright/web-components';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers';
import getConstants, { HeaderType } from '../util/constants';
import { CheckoutType } from '@payright/types-bnpl-checkout-api';
import { LinkLogout } from '../components/links/LinkLogout';

const { CUSTOMER_HUB_URL, HEADER } = getConstants();

interface LargeHeaderWithNavProps {
  children: React.ReactNode;
}

const SCLargeHeaderWithNav = styled.div`
  background-color: ${(props) => props.theme.colours.white};
`;

const LargeHeaderWithNav = ({ children }: LargeHeaderWithNavProps) => {
  const history = useHistory();
  const pathName = history.location.pathname;

  const globalAuthState = useSelector((state: RootState) => state.auth);
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);
  const globalCustomerState = useSelector((state: RootState) => state.customer);
  const globalPaymentState = useSelector((state: RootState) => state.payment);
  const globalPaymentMethodsState = useSelector((state: RootState) => state.paymentMethods);

  const isLoggedIn = globalAuthState.isLoggedIn;
  const checkoutType = globalCheckoutState.checkout?.attributes?.type || CheckoutType.UNDEFINED;

  const loadingAuth = globalAuthState.loading;
  const loadingCheckout = globalCheckoutState.loading;
  const loadingPayment = globalPaymentState.loading;
  const loadingPaymentMethods = globalPaymentMethodsState.loading;

  const userAccount = {
    firstName: globalCustomerState.customerDetails.firstName,
  };

  useEffect(() => {
    if (!globalAuthState) {
      history.push('/');
    }
  }, [globalAuthState, history]);

  return (
    <SCLargeHeaderWithNav>
      <div data-testid="nav">
        <EcommerceNavigation
          userAccount={isLoggedIn ? userAccount : false}
          links={
            <>
              <a target="_blank" rel="noopener noreferrer" href={CUSTOMER_HUB_URL}>
                Your account
              </a>
              <LinkLogout />
            </>
          }
        />
      </div>

      {loadingAuth || loadingCheckout || loadingPaymentMethods || loadingPayment
        ? null
        : renderHeader(pathName, HEADER, checkoutType)}
      {children}
    </SCLargeHeaderWithNav>
  );
};

// Render the header based upon the path
const renderHeader = (pathName: string, HEADER: HeaderType, checkoutType: String) => {
  const {
    CUSTOMER_DETAILS,
    HOUSEHOLD_EXPENSES,
    BANK_STATEMENTS,
    BANK_STATEMENTS_ONLINE_BANKING,
    BANK_STATEMENTS_PDF_INSTRUCTIONS,
    BANK_STATEMENTS_PDF_UPLOAD,
    SUMMARY,
    INCOME_AND_EXPENSES,
    PAYMENT,
    LOAN_PURPOSE,
    CONTRACT,
    ERROR,
  } = HEADER;

  switch (pathName) {
    case CUSTOMER_DETAILS.PAGE_NAME:
      return (
        <HeaderEcommerce title={CUSTOMER_DETAILS.TITLE} subtitle={CUSTOMER_DETAILS.SUBTITLE} />
      );
    case HOUSEHOLD_EXPENSES.PAGE_NAME:
      return (
        <HeaderEcommerce title={HOUSEHOLD_EXPENSES.TITLE} subtitle={HOUSEHOLD_EXPENSES.SUBTITLE} />
      );
    case BANK_STATEMENTS.PAGE_NAME:
      return <HeaderEcommerce title={BANK_STATEMENTS.TITLE} subtitle={BANK_STATEMENTS.SUBTITLE} />;
    case BANK_STATEMENTS_ONLINE_BANKING.PAGE_NAME:
      return (
        <HeaderEcommerce
          title={BANK_STATEMENTS_ONLINE_BANKING.TITLE}
          subtitle={BANK_STATEMENTS_ONLINE_BANKING.SUBTITLE}
        />
      );
    case BANK_STATEMENTS_PDF_INSTRUCTIONS.PAGE_NAME:
      return (
        <HeaderEcommerce
          title={BANK_STATEMENTS_PDF_INSTRUCTIONS.TITLE}
          subtitle={BANK_STATEMENTS_PDF_INSTRUCTIONS.SUBTITLE}
        />
      );
    case BANK_STATEMENTS_PDF_UPLOAD.PAGE_NAME:
      return (
        <HeaderEcommerce
          title={BANK_STATEMENTS_PDF_UPLOAD.TITLE}
          subtitle={BANK_STATEMENTS_PDF_UPLOAD.SUBTITLE}
        />
      );
    case LOAN_PURPOSE.PAGE_NAME:
      return <HeaderEcommerce title={LOAN_PURPOSE.TITLE} subtitle={LOAN_PURPOSE.SUBTITLE} />;
    case CONTRACT.PAGE_NAME:
      return <HeaderEcommerce title={CONTRACT.TITLE} subtitle={CONTRACT.SUBTITLE} />;
    case INCOME_AND_EXPENSES.PAGE_NAME:
      return (
        <HeaderEcommerce
          title={INCOME_AND_EXPENSES.TITLE}
          subtitle={INCOME_AND_EXPENSES.SUBTITLE}
        />
      );

    case SUMMARY.PAGE_NAME:
      if (
        (checkoutType === CheckoutType.APPLY_AND_PAY || CheckoutType.STANDARD) &&
        checkoutType !== CheckoutType.PRE_APPROVAL
      ) {
        return <HeaderEcommerce title={SUMMARY.TITLE} subtitle={SUMMARY.SUBTITLE_STANDARD} />;
      } else {
        return <HeaderEcommerce title={SUMMARY.TITLE} subtitle={SUMMARY.SUBTITLE_PRE_APPROVAL} />;
      }

    case PAYMENT.PAGE_NAME:
      if (
        (checkoutType === CheckoutType.APPLY_AND_PAY || CheckoutType.STANDARD) &&
        checkoutType !== CheckoutType.PRE_APPROVAL
      ) {
        return <HeaderEcommerce title={PAYMENT.TITLE_STANDARD} />;
      } else {
        return (
          <HeaderEcommerce
            title={PAYMENT.TITLE_PRE_APPROVAL}
            subtitle={PAYMENT.SUBTITLE_PRE_APPROVAL}
          />
        );
      }

    case ERROR.PAGE_NAME:
      return <HeaderEcommerce title={ERROR.TITLE} subtitle={ERROR.SUBTITLE} />;

    default:
      return <HeaderEcommerce title="" subtitle="" />;
  }
};

export default LargeHeaderWithNav;
